// Rastreo de paquetería (17TRACK) — tipos, normalizadores y hooks.
//
// La API guarda el detalle en `carrier_trackings` y espeja un resumen en
// `shipments.carrier_tracking` (contrato compartido con la extensión WAYAK
// Monitor: { number, carrier_code, carrier_name, status, sub_status,
// last_event, updated_at }). Endpoints: /carrierTracking[/carriers|/refresh].
import { useMutation, useQuery } from "@tanstack/react-query";
import api from "../../utils/api";
import { getJson } from "../../lib/http";
import { useInvalidateDomain } from "../../lib/invalidation";
import { useCompaniesStore } from "../../store/admin/companiesStore";

export const CARRIER_STATUSES = [
  "pending",
  "info_received",
  "in_transit",
  "out_for_delivery",
  "delivered",
  "exception",
  "expired",
  "not_found",
] as const;

export type CarrierStatus = (typeof CARRIER_STATUSES)[number];

export const isCarrierStatus = (value: unknown): value is CarrierStatus =>
  (CARRIER_STATUSES as readonly unknown[]).includes(value);

/** Refresco del detalle y de la tarjeta de paquetería mientras están abiertos. */
export const CARRIER_REFETCH_MS = 60_000;

export interface CarrierEvent {
  time: string | null;
  description: string;
  location: string | null;
}

export interface CarrierTrackingSummary {
  number: string;
  carrier_code: number | null;
  carrier_name: string | null;
  status: CarrierStatus;
  sub_status: string | null;
  last_event: CarrierEvent | null;
  updated_at: string | null;
}

export interface CarrierTrackingRecord extends CarrierTrackingSummary {
  shipment_id: string;
  events: CarrierEvent[];
  registered_at: string | null;
  last_sync_at: string | null;
  webhook_last_at: string | null;
}

export interface CarrierOption {
  code: number;
  name: string;
  country: string | null;
}

const asRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === "object" ? (value as Record<string, unknown>) : {};

const asStringOrNull = (value: unknown): string | null =>
  typeof value === "string" && value.trim() ? value : null;

const asCode = (value: unknown): number | null => {
  const n = typeof value === "string" ? Number(value) : value;
  return typeof n === "number" && Number.isInteger(n) && n > 0 ? n : null;
};

const normalizeCarrierEvent = (value: unknown): CarrierEvent | null => {
  const raw = asRecord(value);
  const description = typeof raw.description === "string" ? raw.description : "";
  const time = asStringOrNull(raw.time);
  if (!description && !time) return null;
  return { time, description, location: asStringOrNull(raw.location) };
};

export const normalizeCarrierSummary = (value: unknown): CarrierTrackingSummary | null => {
  const raw = asRecord(value);
  const number = asStringOrNull(raw.number ?? raw.tracking_number);
  if (!number) return null;
  return {
    number,
    carrier_code: asCode(raw.carrier_code),
    carrier_name: asStringOrNull(raw.carrier_name),
    status: isCarrierStatus(raw.status) ? raw.status : "pending",
    sub_status: asStringOrNull(raw.sub_status),
    last_event: normalizeCarrierEvent(raw.last_event),
    updated_at: asStringOrNull(raw.updated_at),
  };
};

const normalizeCarrierRecord = (value: unknown): CarrierTrackingRecord | null => {
  const raw = asRecord(value);
  const summary = normalizeCarrierSummary(raw);
  if (!summary) return null;
  return {
    ...summary,
    shipment_id: typeof raw.shipment_id === "string" ? raw.shipment_id : "",
    events: (Array.isArray(raw.events) ? raw.events : [])
      .map(normalizeCarrierEvent)
      .filter((e): e is CarrierEvent => e !== null),
    registered_at: asStringOrNull(raw.registered_at),
    last_sync_at: asStringOrNull(raw.last_sync_at),
    webhook_last_at: asStringOrNull(raw.webhook_last_at),
  };
};

/** Código estable de error de la API (`meta.code`), si viene. */
export const carrierErrorCode = (error: unknown): string | null => {
  const code = (error as { response?: { data?: { meta?: { code?: unknown } } } })?.response?.data
    ?.meta?.code;
  return typeof code === "string" ? code : null;
};

const httpStatus = (error: unknown): number | null => {
  const status = (error as { response?: { status?: unknown } })?.response?.status;
  return typeof status === "number" ? status : null;
};

interface Envelope {
  data?: unknown;
}

export const carrierTrackingKey = (cid: string | null, shipmentId: string | null) =>
  ["shipments", "carrierTracking", cid, shipmentId] as const;

/**
 * Detalle de la guía ligada al envío. `null` si el envío no tiene guía (404);
 * el error 501 (17TRACK sin configurar) se expone para avisarlo en la tarjeta.
 */
export function useCarrierTracking(shipmentId: string | null | undefined, enabled = true) {
  const cid = useCompaniesStore((s) => s.companyId);
  return useQuery({
    queryKey: carrierTrackingKey(cid, shipmentId ?? null),
    enabled: !!shipmentId && enabled,
    refetchInterval: CARRIER_REFETCH_MS,
    retry: (count, error) => {
      const status = httpStatus(error);
      return status !== 404 && status !== 501 && count < 2;
    },
    queryFn: async ({ signal }) => {
      try {
        const body = await getJson<Envelope>("/carrierTracking", { shipment_id: shipmentId }, { signal });
        return normalizeCarrierRecord(body.data);
      } catch (error) {
        if (httpStatus(error) === 404) return null;
        throw error;
      }
    },
  });
}

/** Paqueterías para el selector (búsqueda en el servidor, con caché). */
export function useCarrierOptions(search: string, enabled = true) {
  const term = search.trim();
  return useQuery({
    queryKey: ["carrierTrackingCarriers", term.toLowerCase()],
    enabled,
    staleTime: 60 * 60 * 1000,
    retry: false,
    queryFn: ({ signal }): Promise<CarrierOption[]> =>
      getJson<Envelope>("/carrierTracking/carriers", term ? { search: term, limit: 50 } : { limit: 50 }, {
        signal,
      }).then((body) => {
        // `data` es el arreglo; se tolera el sobre viejo `{ carriers }`.
        const list = Array.isArray(body.data) ? body.data : asRecord(body.data).carriers;
        return (Array.isArray(list) ? list : [])
          .map((item) => {
            const raw = asRecord(item);
            const code = asCode(raw.code);
            const name = asStringOrNull(raw.name);
            return code && name ? { code, name, country: asStringOrNull(raw.country) } : null;
          })
          .filter((c): c is CarrierOption => c !== null);
      }),
  });
}

export function useAttachCarrierTracking() {
  const invalidateDomain = useInvalidateDomain();
  return useMutation({
    mutationFn: (input: { shipment_id: string; tracking_number: string; carrier_code?: number | null }) =>
      api
        .post("/carrierTracking", {
          shipment_id: input.shipment_id,
          tracking_number: input.tracking_number.trim(),
          ...(input.carrier_code ? { carrier_code: input.carrier_code } : {}),
        })
        .then((res) => normalizeCarrierRecord(res.data?.data)),
    onSuccess: () => invalidateDomain("shipments"),
  });
}

export function useRefreshCarrierTracking() {
  const invalidateDomain = useInvalidateDomain();
  return useMutation({
    mutationFn: (shipmentId: string) =>
      api
        .post("/carrierTracking/refresh", { shipment_id: shipmentId })
        .then((res) => normalizeCarrierRecord(res.data?.data)),
    onSuccess: () => invalidateDomain("shipments"),
  });
}

export function useDetachCarrierTracking() {
  const invalidateDomain = useInvalidateDomain();
  return useMutation({
    mutationFn: (shipmentId: string) =>
      api.delete("/carrierTracking", { params: { shipment_id: shipmentId } }).then((res) => res.data),
    onSuccess: () => invalidateDomain("shipments"),
  });
}
